/**
 * Events, Sampling & Promotions — 10 documents.
 *
 * The category where marketing meets food safety. Sampling puts your product directly into a
 * stranger's mouth in a place you do not control; demos put staff you may not employ behind a table
 * inside someone else's store; and prize promotions carry registration and eligibility requirements
 * that vary by jurisdiction and are missed by small brands more often than any other single thing
 * in this library.
 */
export const EVENTS_DOCS = [
  {
    id: "event-participation-agreement",
    name: "Event Participation Agreement",
    category: "events",
    subcategory: "Events and activations",
    aliases: [
      "Exhibitor Agreement",
      "Trade Show Agreement",
      "Vendor Booth Agreement",
      "Market Stall Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Event Organiser",
    whenYouNeedIt:
      "When you book a booth, stall or table: a trade show, a food festival, a farmers market, a pop-up, a conference. The organiser's terms usually arrive as a link at the bottom of a booking confirmation.",
    whatItDoes:
      "Sets what you get for the fee — space, dates, load-in and load-out, power, water, storage — and what the organiser requires from you: permits, insurance, food handling arrangements, waste disposal and conduct on site.",
    whyItMatters:
      "Event terms look administrative and are where the real costs hide. The booth fee is rarely the largest number: electricity, refrigeration, drayage, storage, waste disposal and the organiser's insurance requirements can together exceed it. And if you are handing prepared food or drink to the public, the permit, handwashing and temperature conditions come from the local authority as well as the organiser, and neither is waived because you already shipped the pallets.",
    majorTerms: [
      "Space, location and stated dimensions",
      "Dates, load-in and load-out windows",
      "Total fee and exactly what it includes",
      "Power, water, refrigeration and connection charges",
      "Drayage, material handling and on-site storage",
      "Insurance limits and additional insured requirements",
      "Permits, licences and food-handling arrangements",
      "Whether sampling and on-site selling are permitted",
      "Waste and grey water disposal",
      "Cancellation, postponement and refund terms",
      "Category exclusivity or competitor placement",
      "Use of your marks in event marketing",
      "Liability allocation and indemnity",
    ],
    clausesToWatch: [
      {
        clause: "What the fee actually includes",
        why: "Power, refrigeration, drayage and storage are usually separate charges that arrive after you have committed, and together they routinely exceed the booth fee.",
      },
      {
        clause: "Cancellation and postponement",
        why: "An organiser's right to postpone or relocate without refund leaves you carrying non-refundable travel, product and staff costs.",
      },
      {
        clause: "Insurance and additional insured requirements",
        why: "Limits and endorsements demanded by the venue and the organiser can exceed what your policy carries, and you find out at load-in.",
      },
      {
        clause: "Whether sampling and selling are permitted",
        why: "An exhibitor agreement that allows display but not sampling or on-site sale removes the entire reason you booked the space.",
      },
      {
        clause: "Who obtains permits and meets handling requirements",
        why: "If the agreement is silent it is you, and the requirement is usually set locally rather than by the organiser.",
      },
      {
        clause: "Category exclusivity and competitor placement",
        why: "Paying for a premium position and being placed between two direct competitors is a common and entirely avoidable surprise.",
      },
      {
        clause: "Indemnity and liability",
        why: "A broad indemnity running only one way puts the whole cost of any incident at your booth on you.",
      },
      {
        clause: "Use of your marks in event marketing",
        why: "Without limits your brand can appear in promotion for a sponsor tier or a partner you never agreed to be associated with.",
      },
    ],
    cpgIssues: [
      "Sampling food or beverage is almost always a permit question decided locally, and the organiser's approval is not the same thing as the local authority's.",
      "Handwashing stations, gloves, sanitiser, thermometers and covered transport are conditions of serving samples at most food events, and each takes money and space.",
      "Refrigeration and ice are the biggest hidden operating cost for chilled or frozen product outdoors, and power drops are usually charged per connection.",
      "Product for a multi-day event has to survive the whole event, so a temperature plan and a decision about when to stop serving matter more than the display build.",
      "Allergen information has to be available at the table in a format someone can read while holding a cup.",
      "Sampled product left over at the end usually cannot go back into saleable inventory, so build the write-off into the event cost rather than discovering it afterwards.",
      "Freight, pallets and drayage for a heavy product like beverage can cost more than the booth, and drayage is often billed by the venue at rates nobody negotiates.",
    ],
    founderMistakes: [
      "Booking a booth on the fee alone, then paying again for power, drayage, storage and refrigeration.",
      "Arriving at a food festival without the local permit because the organiser's confirmation never mentioned one.",
      "Discovering at load-in that a required insurance endorsement was not on the policy, and buying cover on the spot.",
      "Paying for a premium stand and being placed between two direct competitors with no separation agreed.",
      "Not budgeting the product given away, and treating a full pallet of samples as an afterthought.",
      "Losing a full event's product when an outdoor cooler failed overnight with no backup plan.",
    ],
    negotiationPoints: [
      "Ask for a fully loaded quote including power, water, drayage, storage and waste before signing anything.",
      "Ask in writing what the organiser requires and what the local authority requires, and who obtains each permit.",
      "Confirm insurance limits and endorsements against your actual policy before load-in rather than on the day.",
      "Ask for category separation on the floor plan if competitors will be present.",
      "Ask for a stated refund or credit position if the event is cancelled, postponed or materially moved.",
      "Get written confirmation that sampling and on-site selling are allowed.",
    ],
    redFlags: [
      "A fee quoted with no schedule of additional charges.",
      "No refund or credit if the organiser cancels or postpones.",
      "Insurance requirements you have not checked against your policy.",
      "Silence on who obtains food permits and who meets handling requirements.",
      "An indemnity running only in the organiser's favour.",
      "No confirmation that sampling or selling is permitted at all.",
    ],
    beforeYouSign: [
      "A fully loaded event cost: product, freight, drayage, power, cold chain, staff and travel.",
      "The permit and food-handling requirements for that specific location, confirmed in writing.",
      "Your certificate of insurance checked against the required limits and endorsements.",
      "The floor plan and your position on it.",
    ],
    related: [
      "event-sampling-agreement",
      "event-sponsorship-contract",
      "demo-contractor-agreement",
      "product-sampling-release",
      "sampling-agreement",
    ],
    attorneyReview: "yes",
    templateId: "event-participation-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "event-sampling-agreement",
    name: "Event Sampling Agreement",
    category: "events",
    subcategory: "Sampling and demos",
    aliases: [
      "Sampling Activation Agreement",
      "Field Marketing Sampling Agreement",
      "Event Sampling Program Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Event Organiser, Venue or Sampling Agency",
    whenYouNeedIt:
      "When you sample outside your own booth: a festival activation, a gym or campus program, a retailer's parking lot, a race, a music event, or an agency running sampling for you across multiple sites.",
    whatItDoes:
      "Sets who runs the sampling and where, who supplies and stores the product, the conditions it is served under, what information is available to the public at the table, and who is responsible when something goes wrong.",
    whyItMatters:
      "Sampling is the most effective marketing a small food or beverage brand has, and the only marketing that involves putting your product directly into a stranger's mouth in an environment you do not control. Two things decide whether it goes well: whether the product stayed inside its temperature and handling conditions all the way to the cup, and whether the person handing it over can answer an ingredient question. Neither is solved by the creative brief, and neither is anybody's job unless the agreement says so.",
    majorTerms: [
      "Locations, dates and hours",
      "Who staffs the activation and who supervises",
      "Product volume, format and sample size",
      "Delivery, storage and temperature control on site",
      "Handwashing, hygiene and food handling arrangements",
      "Permits and who obtains them at each site",
      "Ingredient and allergen information at the point of service",
      "Equipment, ice, cups and waste disposal",
      "Insurance and additional insured status",
      "Reporting: samples served, redemption, feedback",
      "Photography and content rights from the activation",
      "Handling of unused product",
    ],
    clausesToWatch: [
      {
        clause: "Who is responsible for permits and food handling",
        why: "An agency running your activation may assume you handled it while you assumed the opposite, and the sampling stops at the gate.",
      },
      {
        clause: "Temperature control from delivery to service",
        why: "Product held in a car boot for six hours is a food safety problem that arrives days later as a consumer complaint with your name on it.",
      },
      {
        clause: "Allergen and ingredient information at the table",
        why: "Someone always asks, and the person serving needs a written answer rather than a recollection of the label.",
      },
      {
        clause: "Who staffs and supervises",
        why: "Responsibility for how the product is handled follows whoever is standing behind the table, so it has to be assigned rather than assumed.",
      },
      {
        clause: "Insurance and additional insured status",
        why: "Venues and organisers commonly require specific limits, and an unverified assumption becomes your gap on the day.",
      },
      {
        clause: "Reporting obligations",
        why: "Without a samples-served count and a redemption path you have spent product for a number nobody can check.",
      },
      {
        clause: "Content and photography rights",
        why: "Footage from an activation is often the best creative you get all year, and rights to it are rarely automatic.",
      },
    ],
    cpgIssues: [
      "Chilled, frozen and live-culture products need a verified cold chain to the point of service, and a cooler with some ice in it is not a temperature record.",
      "Open sampling of unpackaged product raises handling requirements that differ by location, and someone has to actually hold whatever certification the venue asks for.",
      "Allergen information must be present in writing at the table, not on the phone of the staffer who has gone for a break.",
      "Cups, lids, ice, water and waste disposal are real per-event costs that get left out of the budget entirely.",
      "Product used for sampling should be drawn against a lot code and recorded, so a withdrawal can reach the units that went out at events.",
      "Opened or unrefrigerated leftovers cannot return to saleable stock, so the write-off belongs in the event cost.",
      "Sampling near a retail account works far better when the store has stock and a display, so activations should be scheduled against inventory rather than against a calendar.",
    ],
    founderMistakes: [
      "Sending product to an activation in an unrefrigerated van and having it served warm all afternoon.",
      "Assuming the sampling agency had obtained the permit, and being shut down an hour into the event.",
      "Sampling with no allergen information at the table and having no answer for the first person who asked.",
      "Running a large festival activation with no redemption code, so nothing could be attributed afterwards.",
      "Setting no reporting requirement and receiving a photo album instead of a samples-served count.",
      "Sampling outside a retailer that was out of stock, converting real interest into nothing.",
    ],
    negotiationPoints: [
      "Assign permit responsibility, hygiene and food handling arrangements in writing before booking.",
      "Specify temperature conditions from delivery through service, and require a record to be kept.",
      "Require printed ingredient and allergen information at every table.",
      "Make samples-served counts, redemption data and store-level feedback a deliverable rather than a favour.",
      "Agree who owns photography and video from the activation and how you may use it.",
      "Confirm the insurance limits and endorsements the venue requires before the first date.",
    ],
    redFlags: [
      "No allocation of responsibility for permits, handling or hygiene.",
      "No temperature plan for a chilled or heat-sensitive product.",
      "No allergen information required at the point of service.",
      "Reporting limited to a photo recap.",
      "Product supplied with no lot record.",
      "Insurance requirements nobody has checked against your policy.",
    ],
    beforeYouSign: [
      "A full per-event cost: product, freight, cold chain, staff, supplies, permits and fees.",
      "Written confirmation of who obtains permits and who meets handling requirements at each site.",
      "The ingredient and allergen sheet you will use at the table.",
      "Confirmed inventory at the nearest retail accounts for the activation dates.",
    ],
    related: [
      "product-sampling-release",
      "demo-contractor-agreement",
      "sampling-agreement",
      "sampling-agency-agreement",
      "demo-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "event-brand-ambassador-agreement",
    name: "Event Brand Ambassador Agreement",
    category: "events",
    subcategory: "Sampling and demos",
    aliases: [
      "Field Marketing Ambassador Agreement",
      "Event Staff Agreement",
      "Brand Rep Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Ambassador (or Staffing Agency)",
    whenYouNeedIt:
      "When you engage people to represent the brand at events: pouring at festivals, running a booth, staffing a gym or campus program, or working a market on your behalf.",
    whatItDoes:
      "Sets the engagement: dates and hours, pay, what they are permitted to say about the product, appearance and conduct standards, who supervises them, and who owns the photographs and video they generate.",
    whyItMatters:
      "Event staff are the only person most consumers will ever meet from your brand, and they are typically hired fast, paid hourly, briefed verbally and asked to talk about something people are about to swallow. Two exposures follow. The first is the claim an ambassador makes about what the product does, which is a statement made to sell it whether or not you scripted it. The second is how the relationship is structured when you set the schedule, the uniform and the pitch — a question worth putting to counsel before you build a standing team.",
    majorTerms: [
      "Dates, locations and shift hours",
      "Rate, expenses and payment timing",
      "Scope: pouring, selling, demoing, data capture",
      "Approved talking points and prohibited claims",
      "Appearance, uniform and conduct standards",
      "Who supervises on site and who they report to",
      "Food handling requirements and any certification needed",
      "Social posting and disclosure expectations",
      "Content ownership and likeness release",
      "Confidentiality",
      "Product, cash and equipment accountability",
      "Cancellation, no-show and rescheduling terms",
      "How the working relationship is described and structured",
    ],
    clausesToWatch: [
      {
        clause: "Prohibited claims and approved talking points",
        why: "An ambassador improvising a health benefit at a table is making a claim on your behalf to sell your product.",
      },
      {
        clause: "How the work is scheduled and supervised",
        why: "The more you control hours, methods and appearance, the less the arrangement resembles the one the document describes, which is a question for counsel rather than for the template.",
      },
      {
        clause: "Likeness and content release",
        why: "Photographs and video of an ambassador with your product are unusable in advertising without a release covering the use you actually intend.",
      },
      {
        clause: "Social posting and disclosure",
        why: "Staff posting about the brand are in a material relationship, and the disclosure standard has to be set for them as well as for creators.",
      },
      {
        clause: "Food handling requirements",
        why: "Whoever hands prepared product to the public has to meet the local handling requirement, and the agreement should say who arranges and pays for it.",
      },
      {
        clause: "Product, cash and equipment accountability",
        why: "Cases, coolers, tents and float money disappear at events unless somebody is named as responsible for them.",
      },
      {
        clause: "Cancellation and no-show terms",
        why: "An event staffed by nobody costs you the booth fee, the travel and the product simultaneously.",
      },
    ],
    cpgIssues: [
      "Anyone pouring or portioning food or beverage for the public may need a local handling certification, which is a scheduling constraint as much as a paperwork one.",
      "Ambassadors are asked constantly what the product does, and for supplements and functional beverages the answer the brand can stand behind is narrower than the enthusiasm in the room.",
      "Allergen questions arrive at every sampling table, and staff need a written sheet rather than their own memory of the label.",
      "Temperature control during a shift is the ambassador's job in practice, so the brief has to include when to stop serving.",
      "Ambassadors often handle the only inventory at an event, and unrecorded product going out means no traceability for the units sampled.",
      "Data capture at events builds a marketing list under whatever wording the ambassador used, so the wording has to be yours and written down.",
      "Event staff post about the brand constantly, and for an ingestible product an unscripted post carries the same claim exposure as a paid creator's.",
    ],
    founderMistakes: [
      "Briefing ambassadors verbally and discovering they had been describing the product as a remedy for a specific condition.",
      "Running a standing, closely supervised event team on the same one-page form used for a one-off shift, without ever reviewing the arrangement.",
      "Using event photographs in paid advertising with no likeness release from the people in them.",
      "Sending staff to pour without checking whether a local handling certification was required.",
      "Having no allergen sheet on site and leaving the answer to whoever happened to be on shift.",
      "Losing coolers, tents and cases at events because accountability was never written down.",
    ],
    negotiationPoints: [
      "Provide written talking points and a prohibited-claims list, and require them to be used.",
      "Take a likeness and content release covering advertising use, not just organic posting.",
      "Set the disclosure standard for any social posting about the brand.",
      "Assign responsibility for handling certification and confirm it before the first shift.",
      "Ask counsel how to structure the relationship if the same people work regular, directed shifts.",
      "Make product, cash and equipment accountability explicit, with a return process.",
    ],
    redFlags: [
      "No written talking points or claims limits for an ingestible product.",
      "No likeness release where you intend to use event content in advertising.",
      "A standing team engaged on paperwork nobody has reviewed against how the work is actually run.",
      "No allergen information provided to staff.",
      "Nothing said about social posting or disclosure.",
      "No accountability for product, equipment or cash handled at events.",
    ],
    beforeYouSign: [
      "Written talking points, a prohibited-claims list and an allergen sheet.",
      "A likeness and content release covering the uses you actually intend.",
      "Confirmation of any handling certification required at your event locations.",
      "Counsel's view on how to structure the engagement if the same people work regular supervised shifts.",
    ],
    related: [
      "brand-ambassador-agreement",
      "ambassador-agreement",
      "demo-contractor-agreement",
      "nil-release",
      "influencer-disclosure",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "event-sponsorship-contract",
    name: "Event Sponsorship Contract",
    category: "events",
    subcategory: "Sponsorship and promotions",
    aliases: [
      "Festival Sponsorship Contract",
      "Title Sponsorship Agreement",
      "Pouring Rights Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Event Organiser or Rights Holder",
    whenYouNeedIt:
      "When you pay an event, festival, race, team or venue for association: signage, category exclusivity, pouring rights, on-site sampling, activation space or naming.",
    whatItDoes:
      "Buys a defined package of rights for a fee, and sets out exactly what you receive and when, what you must supply in return, and what happens if the event shrinks, moves or does not happen at all.",
    whyItMatters:
      "A sponsorship is worth precisely what is written into the schedule of rights and nothing more. The recurring failure is category exclusivity that was never properly defined, or that gets quietly sold around: you pay to be the only energy drink and a competitor appears as a hydration partner, or an athlete on the roster has their own beverage deal, or the food trucks pour something else. Add the free product you supply and the cost of building and staffing the activation, and a sponsorship routinely costs twice its headline number.",
    majorTerms: [
      "Fee, payment schedule and what triggers each payment",
      "Schedule of rights: signage, logo placement, announcements, digital",
      "Category exclusivity and exactly how the category is defined",
      "Pouring or on-site sales rights",
      "Activation space, size and location",
      "Free product supplied, and a cap on it",
      "Hospitality, tickets and passes",
      "Use of the event's marks, and of yours",
      "Content and photography rights from the event",
      "Attendance or format assumptions and what happens if they are missed",
      "Cancellation, postponement and force majeure",
      "Renewal, right of first refusal and rate protection",
      "Approval rights over how your brand is used",
    ],
    clausesToWatch: [
      {
        clause: "How the exclusive category is defined",
        why: "Energy drink and functional beverage are different categories in a competitor's contract, and the gap between the two definitions is how they get on site.",
      },
      {
        clause: "Whether exclusivity binds the organiser's partners",
        why: "Exclusivity that does not reach food trucks, bars, talent and media partners is exclusivity in name only.",
      },
      {
        clause: "Free product volume",
        why: "An uncapped commitment to supply product for the event can quietly exceed the sponsorship fee itself.",
      },
      {
        clause: "What happens if the event shrinks or moves",
        why: "Attendance and format assumptions belong in the contract, or a much smaller event delivers a fraction of what you paid for.",
      },
      {
        clause: "Cancellation and force majeure",
        why: "A fee paid in full with no refund or credit if the event does not happen puts the whole risk on the sponsor.",
      },
      {
        clause: "Approval over use of your brand",
        why: "Your logo can otherwise appear beside partners, claims or content you would never have chosen to stand next to.",
      },
      {
        clause: "Renewal and rate protection",
        why: "Without a right of first refusal, a successful activation is mostly an invitation for someone else to outbid you next year.",
      },
      {
        clause: "Content rights from your own activation",
        why: "Footage and photography of your activation are often the most valuable thing you take away, and the rights are rarely automatic.",
      },
    ],
    cpgIssues: [
      "Pouring rights at a venue are a distribution question as much as a brand question, since somebody has to deliver, chill and re-stock all season.",
      "Free product for a festival is a real production cost, so convert the case commitment into cost of goods before agreeing the fee.",
      "Chilled and frozen product at an outdoor venue needs cold chain and back-of-house space that a sponsorship package rarely includes by default.",
      "Sampling inside a sponsorship still needs local permits and handling arrangements, and being the sponsor does not supply either.",
      "Category exclusivity should be drafted against how retail defines your set, not how the organiser's sales deck describes it.",
      "Sponsoring an event that serves alcohol changes the audience, the conduct expectations and often the insurance requirements for your activation.",
      "Branded cups, coolers and packaging produced for one event are usually unusable at the next unless the artwork was planned for reuse.",
    ],
    founderMistakes: [
      "Paying for category exclusivity that was never defined, and watching a direct competitor appear under a slightly different label.",
      "Committing to supply product with no cap, and shipping several times the volume budgeted.",
      "Not asking what happens on postponement, and losing the whole fee when the event moved to the following year.",
      "Budgeting the fee alone and then paying again for the activation build, staff, cold chain and freight.",
      "Taking no rights to the photography and video from their own activation.",
      "Sponsoring an event whose audience never overlapped with the accounts they were trying to sell through.",
    ],
    negotiationPoints: [
      "Define the exclusive category in your own words and test it against how competitors describe themselves.",
      "Extend exclusivity to the organiser's vendors, bars, talent and media partners, not just the main stage.",
      "Cap the free product commitment in cases and price it at cost of goods.",
      "Tie part of the fee to the event happening at the stated scale, with a credit if it does not.",
      "Secure rights to photography and video of your own activation.",
      "Ask for a right of first refusal and rate protection on renewal.",
      "Take approval rights over how your brand and logo are used.",
    ],
    redFlags: [
      "Category exclusivity with no definition of the category.",
      "No refund or credit if the event is cancelled or materially reduced.",
      "An uncapped obligation to supply free product.",
      "No rights to content created at your own activation.",
      "Payment in full up front with nothing tied to delivery.",
      "Exclusivity that does not bind vendors, talent or media partners.",
    ],
    beforeYouSign: [
      "The schedule of rights in writing, item by item, with dates and placements.",
      "Total cost: fee, product at cost of goods, activation build, staff, freight and cold chain.",
      "The category definition, tested against the competitors you actually worry about.",
      "Last year's attendance and format, and what the contract says if this year differs.",
    ],
    related: [
      "event-sponsorship-agreement",
      "sponsorship-agreement",
      "promotional-agreement",
      "event-participation-agreement",
      "event-sampling-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "promotional-agreement",
    name: "Promotional Agreement",
    category: "events",
    subcategory: "Sponsorship and promotions",
    aliases: [
      "Promotion Agreement",
      "Joint Promotion Agreement",
      "Marketing Promotion Contract",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Promotional Partner, Agency or Retailer",
    whenYouNeedIt:
      "When you run a promotion with somebody else: a partner brand, a retailer, a venue, a media partner, or an agency executing on your behalf. Anything with a mechanic, a prize or a bundled offer.",
    whatItDoes:
      "Documents the promotion: the mechanic, the dates, who does what, who pays for what, who supplies and fulfils prizes, whose rules govern entry, and who answers a consumer who complains.",
    whyItMatters:
      "Promotions get agreed in a meeting and executed by three different companies, and the failure is almost always a gap rather than a dispute. Nobody wrote the official rules, nobody agreed who fulfils the prize, nobody decided whose list the entries join, and nobody checked whether the mechanic raised registration questions anywhere. When it goes wrong the consumer complains to the brand printed on the pack, which is you, whatever the agency was supposed to be doing.",
    majorTerms: [
      "The mechanic, written out in plain terms",
      "Dates, territories and eligibility",
      "Who drafts and owns the official rules",
      "Prize supply, fulfilment and shipping",
      "Who bears prize costs and freight",
      "Media, creative and asset approvals",
      "Data: who collects entries and who may market to them",
      "Use of each party's marks",
      "Reporting and reconciliation",
      "Consumer enquiry and complaint handling",
      "Rights to amend or cancel the promotion",
      "Liability and indemnity between the parties",
    ],
    clausesToWatch: [
      {
        clause: "Who owns the official rules",
        why: "If neither party drafts them the promotion runs on a caption, and the caption is what a complaining entrant will hold you to.",
      },
      {
        clause: "Prize fulfilment responsibility",
        why: "An unfulfilled prize becomes a public complaint against whichever brand the winner recognises, which is usually the one on the pack.",
      },
      {
        clause: "Data rights over entries",
        why: "Entrants signed up for a promotion, not for two marketing lists, and who may contact them afterwards has to be agreed in advance.",
      },
      {
        clause: "Approval over creative and messaging",
        why: "A partner's copy describing your product can make a claim you would never have made about it yourself.",
      },
      {
        clause: "Whether the mechanic raises registration questions",
        why: "Prize promotions carry registration, bonding and eligibility requirements in some places, and this is the single most common thing small brands miss.",
      },
      {
        clause: "Amendment and cancellation rights",
        why: "Changing a live promotion with no reserved right is how a goodwill gesture turns into a complaint.",
      },
      {
        clause: "Indemnity between the parties",
        why: "Without it, whichever brand the consumer contacts absorbs the entire cost of the other party's mistake.",
      },
    ],
    cpgIssues: [
      "A bundled promotion with another brand puts two ingredient decks into one offer, and the allergen information has to cover both.",
      "Product prizes are cost of goods plus freight, and shipping food or beverage to a winner in another region can cost more than the prize itself.",
      "Promotions requiring a purchase interact with retailer promotional funding and deductions, so the trade spend and the promotion budget need reconciling as one number.",
      "On-pack promotions need artwork, codes, lead time and a packaging run, and the promotion has to end either well before or well after the packs clear the shelf.",
      "A promotion that lifts demand runs straight into your co-packer's lead time, and running out mid-promotion is its own complaint.",
      "Prize product carries the same liability as sold product: allergens, code dates and handling all apply exactly as they would to an order.",
      "Entries collected through a partner's form join a marketing list under wording you did not write, which matters for every channel you use afterwards.",
    ],
    founderMistakes: [
      "Running a promotion off a social caption with no official rules and no eligibility terms at all.",
      "Assuming the partner would fulfil the prizes, and finding out after the winners were announced that nobody had.",
      "Never checking whether the mechanic raised registration questions before promoting it nationally.",
      "Letting the partner keep the entire entry list because data rights were never discussed.",
      "Printing an on-pack promotion whose end date arrived long before the packs cleared the shelf.",
      "Running a promotion that outsold the production run and going out of stock in week two.",
    ],
    negotiationPoints: [
      "Agree in writing who drafts the official rules, and have counsel look at the mechanic before it is promoted.",
      "Assign prize supply, fulfilment and shipping explicitly, including who pays freight.",
      "Agree data rights over entries before the entry form goes live.",
      "Take approval rights over any creative describing your product.",
      "Reserve a right to amend or cancel, and state how that would be communicated.",
      "Name who handles consumer enquiries and complaints, and publish that route.",
      "Check the promotion against your production capacity and lead times before committing.",
    ],
    redFlags: [
      "A promotion with no official rules.",
      "No agreement on who fulfils prizes.",
      "A mechanic taken to market with nobody checking registration or eligibility questions.",
      "Entry data going to a partner with no stated basis for either side to use it.",
      "No approval right over how your product is described.",
      "No named owner for consumer complaints.",
    ],
    beforeYouSign: [
      "The full mechanic written out, including how winners are chosen, notified and fulfilled.",
      "Counsel's view on the mechanic before it is promoted anywhere.",
      "Total cost: prizes at cost of goods, freight, media, creative and agency fees.",
      "Confirmation that production can supply the demand the promotion is designed to create.",
    ],
    related: [
      "contest-rules",
      "sweepstakes-rules",
      "product-giveaway-rules",
      "promotional-services-agreement",
      "promotional-partnership-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-giveaway-rules",
    name: "Product Giveaway Rules",
    category: "events",
    subcategory: "Contests, sweepstakes and giveaways",
    aliases: [
      "Giveaway Terms",
      "Social Giveaway Rules",
      "Free Product Promotion Rules",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Entrants",
    whenYouNeedIt:
      "Before any follow-like-and-tag post, any email-list giveaway, any prize draw at an event. The rules have to exist before the promotion is announced, not after somebody asks for them.",
    whatItDoes:
      "Publishes the terms of a giveaway: who can enter, how, when it opens and closes, how winners are chosen and notified, what the prize is, and what entrants agree to by entering.",
    whyItMatters:
      "A giveaway is the smallest promotion a brand runs and the one most often run with no rules at all, because it looks like a social post rather than a structure. Prize promotions carry registration, eligibility and disclosure requirements that vary by jurisdiction and get missed constantly. A giveaway is also where a brand quietly acquires a large list of people who agreed to enter a draw rather than to hear from a brand — and that arrives later as a deliverability collapse in the week you least want one.",
    majorTerms: [
      "Eligibility: age, location and exclusions",
      "Entry period with time zone",
      "Entry method, and whether a purchase is involved at all",
      "How winners are selected and when",
      "Winner notification and response deadline",
      "Prize description and approximate value",
      "Prize substitution and non-transferability",
      "Who supplies and ships the prize",
      "Data use and marketing consent, kept separate from entry",
      "Platform disclaimers where the promotion runs on social",
      "Publicity and use of the winner's name",
      "Right to amend or cancel",
      "Where the full rules are published",
    ],
    clausesToWatch: [
      {
        clause: "Whether entry requires a purchase",
        why: "Whether a purchase can be a condition of entry is exactly the sort of question that differs by jurisdiction, and it changes what kind of promotion you are running.",
      },
      {
        clause: "Eligibility and territory",
        why: "A giveaway open to everyone is open to places with requirements you never considered, and shipping a consumable across a border is its own problem.",
      },
      {
        clause: "Marketing consent bundled into entry",
        why: "Entering a draw is not the same as asking to hear from a brand, and a list built that way behaves badly the first time you mail it.",
      },
      {
        clause: "Winner selection method",
        why: "We picked our favourite and a random draw are different promotions with different requirements attached to them.",
      },
      {
        clause: "Prize description and value",
        why: "A vaguely described prize invites arguments with winners and makes any substitution look like a switch.",
      },
      {
        clause: "Platform promotion rules",
        why: "Social platforms set their own promotion requirements, and breaking them can cost you the account you were promoting on.",
      },
      {
        clause: "Right to amend or cancel",
        why: "Without one, a promotion that has to be stopped mid-flight has no clean way to end.",
      },
    ],
    cpgIssues: [
      "A food, beverage or supplement prize is a perishable shipped to a stranger, so cold chain, transit time and destination all matter before the prize is announced.",
      "Prize product carries the brand's liability exactly as a sale does: allergen information, code dates and handling all apply.",
      "Prizes including product not sold in the winner's country raise labelling and import questions a giveaway post never anticipates.",
      "Giveaway entries are the largest single source of low-quality addresses in most DTC CPG lists, and mailing them like customers damages deliverability for everyone else on it.",
      "Age-sensitive products need eligibility conditions that the mechanic actually enforces rather than merely states.",
      "Bundled prizes with a partner brand mean two ingredient decks, two sets of allergens and two brands the winner may complain to.",
    ],
    founderMistakes: [
      "Running a giveaway from a caption with no rules, then having no basis for disqualifying an obviously fraudulent entry.",
      "Building an email list from giveaway entries and mailing it as a customer list, with the deliverability consequences that follow.",
      "Announcing a prize open to anyone anywhere, then discovering what it costs to ship a chilled product abroad.",
      "Not checking the platform's own promotion requirements and having the post pulled mid-promotion.",
      "Never taking advice on the mechanic before promoting it nationally.",
      "Describing a prize loosely and ending up arguing with a winner about what they had won.",
    ],
    negotiationPoints: [
      "Have counsel look at the mechanic and the territories before the giveaway is announced.",
      "Publish full rules somewhere durable and link them from every post.",
      "Keep marketing consent separate from entry, and treat giveaway contacts as their own segment.",
      "Restrict eligibility to territories you can actually ship a consumable into.",
      "Describe the prize precisely, including value, and reserve a substitution right.",
      "Reserve the right to amend or cancel, and say how that would be communicated.",
    ],
    redFlags: [
      "A giveaway announced with no published rules.",
      "Entry conditioned on a purchase with nobody checking what that means where you are running it.",
      "Worldwide eligibility for a perishable prize.",
      "Marketing consent bundled into the entry mechanic.",
      "No stated winner selection method or notification deadline.",
      "Rules copied from another brand with their prize and territory still in the text.",
    ],
    beforeYouSign: [
      "The full mechanic written out, including selection, notification and fulfilment.",
      "Counsel's view on the mechanic, the territories and any registration questions.",
      "The prize costed, including shipping a perishable to the furthest eligible address.",
      "The platform's own promotion requirements for wherever you are running it.",
    ],
    related: [
      "contest-rules",
      "sweepstakes-rules",
      "promotional-agreement",
      "email-marketing-consent",
      "sms-marketing-terms",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Prize promotions carry registration, eligibility and disclosure requirements that vary by jurisdiction and change, so the mechanic needs review before the promotion is announced rather than after a complaint.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "contest-rules",
    name: "Contest Rules",
    category: "events",
    subcategory: "Contests, sweepstakes and giveaways",
    aliases: [
      "Official Contest Rules",
      "Skill Contest Terms",
      "Competition Rules",
    ],
    stages: ["launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Entrants",
    whenYouNeedIt:
      "Before you launch anything where entrants compete and are judged: a recipe contest, a photo or video competition, a naming contest, a best-customer-story promotion.",
    whatItDoes:
      "Publishes the terms of a judged competition: eligibility, how to enter, the judging criteria and who judges, the timetable, the prizes, and what rights you take in whatever entrants submit.",
    whyItMatters:
      "A contest is decided on merit, which makes it a different animal from a random draw and brings its own conditions: published criteria, identifiable judges, a way to resolve ties. It also produces something a giveaway does not — a pile of consumer-created content the brand almost always wants to use. If the rules take no licence in entries, the winning recipe or the best video is content you have to go back and ask for, one entrant at a time, after the moment has passed.",
    majorTerms: [
      "Eligibility and exclusions",
      "Entry period, method and format requirements",
      "Judging criteria, weighting and who judges",
      "Timetable for judging, notification and announcement",
      "Tie-break and disqualification process",
      "Prizes, values and fulfilment",
      "Licence taken in submitted entries",
      "Entrant warranties about their own content",
      "Third-party rights in entries: music, images, other people",
      "Publicity rights over winners' names and entries",
      "Data use, kept separate from entry",
      "Right to amend or cancel",
      "Where the full rules are published",
    ],
    clausesToWatch: [
      {
        clause: "Licence in entries",
        why: "Without it you cannot use the winning recipe, photo or video in advertising, which was usually the whole point of running the contest.",
      },
      {
        clause: "Entrant warranties about their content",
        why: "Entries routinely contain other people's music, images and faces, and the warranty is what puts that responsibility where it belongs.",
      },
      {
        clause: "Published judging criteria and judges",
        why: "A judged contest with vague criteria and anonymous judges invites disputes you have no documented way to resolve.",
      },
      {
        clause: "Tie-break and disqualification",
        why: "An unresolved tie, or an obviously ineligible entry with no stated process, becomes a public argument in your own comments.",
      },
      {
        clause: "Publicity rights",
        why: "Announcing a winner by name and showing their entry needs permission taken at entry, not requested afterwards.",
      },
      {
        clause: "Whether the mechanic raises registration questions",
        why: "Prize promotions carry requirements that vary by jurisdiction, and this is the question small brands skip most often.",
      },
      {
        clause: "Right to amend or cancel",
        why: "A contest that has to stop — too few entries, a technical failure, an unusable winner — needs a stated way out of it.",
      },
    ],
    cpgIssues: [
      "Recipe contests invite entrants to combine your product with ingredients you have never tested, and the entries you promote become your content and your implied endorsement.",
      "Recipe and food-preparation entries carry allergen implications once you republish them, so the rules should let you edit or annotate what you publish.",
      "Photo and video entries often show usage that contradicts your own label, which becomes a claim question the moment you amplify them.",
      "Product prizes are cost of goods plus perishable shipping, and a multi-prize contest multiplies that quickly.",
      "Contest content is frequently the best creative a small brand gets all year, which makes the licence clause the most valuable part of the rules.",
      "Entries describing health outcomes are testimonials, and publishing one turns an entrant's words into brand messaging about an ingestible product.",
      "A contest designed to drive purchase interacts with retailer trade spend and stock levels, so the timing has to match inventory.",
    ],
    founderMistakes: [
      "Running a recipe contest and having no licence to publish the winning recipe.",
      "Republishing an entrant's video that contained licensed music, and having it taken down.",
      "Judging on undisclosed criteria and having no answer for an entrant who asked how they lost.",
      "Amplifying a winning entry that made a health claim the brand could not stand behind.",
      "Never obtaining publicity permission and having to pull the winner announcement.",
      "Announcing a national contest without checking whether the mechanic raised registration questions anywhere.",
    ],
    negotiationPoints: [
      "Take a licence in every entry broad enough for the advertising uses you intend, and say so plainly at entry.",
      "Require entrants to warrant the content is theirs and that anyone appearing in it has agreed.",
      "Publish the judging criteria, the judges and the timetable.",
      "Take publicity permission at the point of entry rather than after selection.",
      "Reserve editing rights over anything you republish, particularly recipes and usage content.",
      "Have counsel review the mechanic, eligibility and registration questions before launch.",
    ],
    redFlags: [
      "No licence taken in entries.",
      "No warranty from entrants about third-party content in their submissions.",
      "Undisclosed judging criteria or judges.",
      "No tie-break or disqualification process.",
      "Publicity over winners assumed rather than agreed at entry.",
      "A national contest launched with no review of the mechanic.",
    ],
    beforeYouSign: [
      "The full mechanic, including judging, notification, fulfilment and publication.",
      "Counsel's review of the mechanic, eligibility and registration questions before launch.",
      "The licence wording you need for how you actually intend to use entries.",
      "The prize schedule costed, including perishable shipping.",
    ],
    related: [
      "sweepstakes-rules",
      "product-giveaway-rules",
      "promotional-agreement",
      "ugc-creator-agreement",
      "content-release",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Judged prize promotions carry eligibility, disclosure and registration requirements that vary by jurisdiction, and the rights you take in entries have to be right at entry rather than negotiated afterwards.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sweepstakes-rules",
    name: "Sweepstakes Rules",
    category: "events",
    subcategory: "Contests, sweepstakes and giveaways",
    aliases: [
      "Official Sweepstakes Rules",
      "Prize Draw Rules",
      "Random Draw Promotion Terms",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Entrants",
    whenYouNeedIt:
      "Before you announce any promotion where a prize is awarded by chance rather than judged: a random draw, a scratch-off, an on-pack code draw, an entry-per-purchase promotion.",
    whatItDoes:
      "Publishes the full terms of a chance-based promotion: who may enter, how, over what period, how the draw is conducted and by whom, how winners are notified, the prizes, and the disclosures that accompany the promotion.",
    whyItMatters:
      "Chance-based prize promotions are the most heavily conditioned promotion a consumer brand runs. Registration, bonding, eligibility, disclosure and record-keeping requirements vary by jurisdiction, and small brands miss them routinely because the whole thing looks like marketing rather than administration. The structural point worth knowing is that the relationship between entry, chance and purchase is what determines which requirements apply — which is why the mechanic goes to counsel before the artwork goes to the printer.",
    majorTerms: [
      "Eligibility, age and territory",
      "Entry period with time zone, and entry limits",
      "Every entry route, including any free route",
      "Draw method, timing and who conducts it",
      "Odds statement",
      "Winner notification, response deadline and forfeiture",
      "Prize descriptions, values and fulfilment",
      "Prize substitution and transferability",
      "Winner obligations before the prize is released",
      "Publicity and use of winners' names",
      "Sponsor identification and a contact route",
      "Data use, kept separate from entry",
      "Record keeping and availability of a winners list",
      "Right to amend, suspend or cancel",
    ],
    clausesToWatch: [
      {
        clause: "The relationship between purchase and entry",
        why: "This is the structural feature that decides which set of requirements the promotion sits under, and it cannot be fixed after launch.",
      },
      {
        clause: "Territory and eligibility",
        why: "A promotion open everywhere is subject to requirements in places you never considered and cannot fulfil a perishable prize into.",
      },
      {
        clause: "Draw method and who runs it",
        why: "A draw conducted informally with no record is impossible to stand behind if an entrant challenges the outcome.",
      },
      {
        clause: "Prize value and fulfilment",
        why: "Prize values carry consequences for you and for the winner, and vague valuation causes arguments at exactly the wrong moment.",
      },
      {
        clause: "Winner obligations before release",
        why: "Without documentation requirements you may have no way to verify eligibility before handing over a prize.",
      },
      {
        clause: "Amendment and cancellation rights",
        why: "A promotion hit by a technical failure or obvious abuse needs a stated route to stop rather than an improvised one.",
      },
      {
        clause: "Record keeping",
        why: "Entry records, draw records and winner records are what let you answer a challenge months after the promotion closed.",
      },
    ],
    cpgIssues: [
      "On-pack promotions need artwork, codes, a packaging run and lead time, so the promotion period has to be set against when packs actually clear the shelf rather than when the campaign starts.",
      "An entry-per-purchase mechanic in retail interacts with trade spend, display and stock, so it has to be planned with the retailer rather than around them.",
      "Product prizes are perishable, so cold chain, transit time and destination all matter before the prize is described in the rules.",
      "Age-sensitive products need eligibility conditions the mechanic enforces rather than merely states.",
      "Large draws generate large lists of people who wanted a prize rather than a newsletter, and treating them as customers damages your sending reputation.",
      "A promotion that lifts demand sharply needs production behind it, and a co-packer's lead time is not a marketing variable.",
      "Codes printed on packaging cannot be changed once the run is made, so an error in the mechanic is a packaging write-off as well as a promotion problem.",
    ],
    founderMistakes: [
      "Designing the mechanic first and asking about requirements after the artwork was already at the printer.",
      "Opening a draw worldwide and discovering the shipping, labelling and eligibility problems only once a winner was drawn.",
      "Running a draw with no record of entries and no way to show how the winner was chosen.",
      "Bundling marketing consent into entry and mailing the whole entrant list as though they were customers.",
      "Printing an on-pack promotion whose end date arrived while packs were still on shelf.",
      "Under-forecasting the demand lift and going out of stock while the promotion was still running.",
    ],
    negotiationPoints: [
      "Take the mechanic to counsel before any artwork, code or media is committed.",
      "Restrict the territory to places you can actually fulfil a perishable prize into.",
      "Document the draw: method, date, who conducted it, and the record kept.",
      "Keep marketing consent separate from entry and segment entrant contacts.",
      "Set an on-pack end date that allows for how long packs remain on shelf.",
      "Confirm production capacity and retailer stock before the promotion launches.",
    ],
    redFlags: [
      "A chance-based promotion launched with nobody reviewing the mechanic.",
      "Rules copied from another brand's promotion.",
      "No stated draw method, odds or winner notification process.",
      "Worldwide eligibility for a perishable prize.",
      "On-pack timing set with no allowance for shelf life or sell-through.",
      "No record keeping of entries or of the draw itself.",
    ],
    beforeYouSign: [
      "The complete mechanic written out before any artwork or media is committed.",
      "Counsel's review of eligibility, registration, disclosure and record-keeping questions.",
      "The prize schedule costed, including perishable shipping to the furthest eligible address.",
      "Production and retailer stock confirmation for the whole promotion period.",
    ],
    related: [
      "contest-rules",
      "product-giveaway-rules",
      "promotional-agreement",
      "email-marketing-consent",
      "retail-vendor-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Chance-based prize promotions carry registration, bonding, disclosure and record-keeping requirements that vary by jurisdiction and change — the mechanic must be reviewed before artwork or media is committed.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "demo-contractor-agreement",
    name: "Demo Contractor Agreement",
    category: "events",
    subcategory: "Sampling and demos",
    aliases: [
      "Demonstrator Agreement",
      "Field Demo Staff Agreement",
      "In-Store Sampling Contractor Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Demonstrator",
    whenYouNeedIt:
      "When you engage individuals to run in-store demos for you directly, rather than going through a retailer's own demo program or a staffing agency.",
    whatItDoes:
      "Engages a demonstrator for stated shifts: what they do, what they are paid, whose product and equipment they use, what they may say about the product, and who is responsible for handling, hygiene and conduct inside the store.",
    whyItMatters:
      "Demo staff are the most common place a small brand's paperwork stops matching reality. You post the schedule, name the store, supply the table and the product, script the pitch and require a uniform — and then engage them as a contractor. How that relationship should be structured depends on how the work is actually controlled rather than on what the document is called, the tests differ, and unwinding it later is expensive. The second exposure is what gets said and served: a health claim at the table and an allergen question answered wrong are both yours.",
    majorTerms: [
      "Scope of demo work and stores covered",
      "Shift dates, hours and how they are scheduled",
      "Rate, expenses, mileage and payment timing",
      "Who supplies product, table, equipment and supplies",
      "Approved talking points and prohibited claims",
      "Food handling requirements and who arranges certification",
      "Retailer rules, badging and floor access",
      "Reporting: units sampled, sales, stock levels, store feedback",
      "Insurance and who carries it",
      "Product and equipment accountability",
      "Confidentiality",
      "Content, photography and likeness permission",
      "Term, cancellation and no-show terms",
    ],
    clausesToWatch: [
      {
        clause: "How the work is scheduled, supervised and directed",
        why: "The more the brand controls hours, methods and appearance, the further the arrangement drifts from what the document describes — a question for counsel, not for the template.",
      },
      {
        clause: "Prohibited claims and approved talking points",
        why: "A demonstrator improvising a benefit is making a claim on your behalf, inside a retailer's store, to somebody about to buy.",
      },
      {
        clause: "Food handling and certification",
        why: "Whoever portions and serves product has to meet the local handling requirement, and the agreement should say who arranges and pays for it.",
      },
      {
        clause: "Retailer rules and floor access",
        why: "Retailers set their own insurance, badging and conduct requirements, and a demonstrator turned away at the door is a paid shift with no demo.",
      },
      {
        clause: "Product accountability",
        why: "Demo product is inventory leaving the building, and without a record it is neither costed nor traceable.",
      },
      {
        clause: "Insurance",
        why: "An incident at a demo table inside someone else's store raises the question of whose cover responds, and that is better answered in advance.",
      },
      {
        clause: "Likeness and content permission",
        why: "Photographs and video from demos used in advertising need permission from the people appearing in them.",
      },
      {
        clause: "Cancellation and no-show terms",
        why: "Paid shifts nobody worked and stores expecting a demo that never arrived both cost more than the shift rate.",
      },
    ],
    cpgIssues: [
      "Portioning and serving food in a retail environment usually requires a local handling certification, which is a scheduling constraint as much as a paperwork one.",
      "Allergen and ingredient information has to be at the table in writing, because the question always comes and the demonstrator is not the label.",
      "Chilled and frozen demo product needs temperature control at the table, and a cooler without a thermometer is not a plan.",
      "Demo product is cost of goods given away, so the true cost of a shift is the rate plus the cases consumed plus the supplies.",
      "Demo product should be drawn against a lot code and recorded like any other shipment, so a withdrawal can reach it.",
      "Demonstrators are often the only source of store-level intelligence a small brand gets — stock, facings, competitor activity — and the reporting requirement is where that value is captured or lost.",
      "A demo scheduled at a store that is out of stock turns a paid shift into nothing, so scheduling has to follow inventory rather than the calendar.",
    ],
    founderMistakes: [
      "Building a standing demo team on a one-page form while scheduling, supervising and directing the work closely, and never taking advice on it.",
      "Briefing demonstrators verbally and discovering they had been describing the product as a treatment for something.",
      "Sending staff without checking the retailer's insurance and badging requirements, and having them turned away at the service desk.",
      "Never recording demo product against lot codes, so cases left the building with no traceability and no cost attribution.",
      "Getting no store-level reporting and rebooking the same underperforming stores the next quarter.",
      "Using demo photography in paid ads with no release from the demonstrator.",
    ],
    negotiationPoints: [
      "Take counsel's view on how to structure the engagement before you build a recurring demo team.",
      "Supply written talking points and a prohibited-claims list, and require them to be used.",
      "Assign responsibility for handling certification and confirm it before the first shift.",
      "Make units-sampled, stock-level and store-feedback reporting a deliverable rather than a favour.",
      "Take a likeness and content permission covering advertising use.",
      "Set product accountability: what was drawn, from which lot, and what came back.",
      "Confirm each retailer's floor-access and insurance requirements before booking.",
    ],
    redFlags: [
      "A recurring, closely directed demo team on paperwork nobody has reviewed against how the work actually runs.",
      "No written talking points or claims limits for an ingestible product.",
      "No allergen information provided for the table.",
      "No handling certification arranged for anyone serving product.",
      "Demo product leaving with no lot record or cost attribution.",
      "No reporting requirement at all.",
    ],
    beforeYouSign: [
      "Counsel's view on how you intend to engage, schedule and supervise demo staff.",
      "Written talking points, prohibited claims and an allergen sheet.",
      "Each retailer's floor-access, badging and insurance requirements.",
      "A full per-shift cost model including product, supplies and travel.",
    ],
    related: [
      "demo-agreement",
      "event-brand-ambassador-agreement",
      "independent-contractor-agreement",
      "sampling-agency-agreement",
      "event-sampling-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "How demo staff should be engaged depends on how the work is actually controlled, the tests vary by jurisdiction, and correcting the structure after the fact costs far more than advice before the first shift.",
    templateId: "demo-contractor-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-sampling-release",
    name: "Product Sampling Release",
    category: "events",
    subcategory: "Sampling and demos",
    aliases: [
      "Sampling Waiver",
      "Tasting Release",
      "Sample Participant Release",
    ],
    stages: ["development", "launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Sampling Participant",
    whenYouNeedIt:
      "When participants are identifiable and the sampling is more structured than handing a cup to a passer-by: a tasting panel, a consumer research session, a filmed activation, a private event, a product trial group.",
    whatItDoes:
      "Records that a participant took part voluntarily, what they were told about the product and its ingredients, anything they confirmed about themselves, and — where relevant — permission to film or photograph them and use the result.",
    whyItMatters:
      "Founders reach for a release expecting it to solve the allergy problem, and it does not. What a release can sensibly cover varies, and a signature does not undo an allergen that was never disclosed. What it genuinely does is create a record: what ingredient information was shown, what the participant confirmed, who was present, and whether you may use the footage. The protection that matters is upstream — accurate ingredient and allergen information, visible before anyone tastes anything, and staff who know when to stop serving.",
    majorTerms: [
      "Identification of the product being sampled",
      "The ingredient and allergen information shown to the participant",
      "Participant confirmation of any relevant restrictions",
      "Voluntary participation and the right to stop at any time",
      "Age and eligibility, and adult consent where relevant",
      "Photography, video and likeness permission",
      "How any recorded content may be used",
      "Feedback and whether it may be quoted",
      "Confidentiality where an unlaunched product is involved",
      "Data collected and how it will be used",
      "A contact route for reporting an adverse reaction",
      "Signature and date, or a recorded equivalent",
    ],
    clausesToWatch: [
      {
        clause: "What allergen information was actually presented",
        why: "A release referencing an ingredient sheet nobody displayed is a record of nothing, and this is the part that matters most.",
      },
      {
        clause: "Scope of the likeness and content permission",
        why: "A release covering internal use will not carry a filmed reaction into a paid ad, which is usually exactly where you want it.",
      },
      {
        clause: "Confidentiality for unlaunched product",
        why: "Tasting panels see formulas, packaging and positioning before anyone else, and without a confidentiality term all of it is fair game.",
      },
      {
        clause: "Whether feedback may be quoted",
        why: "A participant's enthusiastic comment becomes a testimonial the moment you publish it, with everything that implies for an ingestible product.",
      },
      {
        clause: "Adverse reaction contact route",
        why: "Somebody needs to be reachable and somebody needs to record what happened, which is a process question as much as a paperwork one.",
      },
      {
        clause: "Age and adult consent",
        why: "Sampling to minors at public events and in schools raises consent questions the form has to handle rather than ignore.",
      },
      {
        clause: "What the release does not cover",
        why: "Treating the form as complete protection is the specific mistake having a form tends to cause.",
      },
    ],
    cpgIssues: [
      "Allergen disclosure has to happen before the sample is handed over, in writing and legible to someone standing at a table — the release records it, it does not replace it.",
      "Unlaunched formulations may contain ingredients that change before launch, so the sheet has to describe what is actually in the cup that day.",
      "Cross-contact in a shared kitchen, a co-packer's line or a caterer's prep area is a real allergen source a formula sheet alone will not capture.",
      "Ingredients people avoid for reasons other than allergy — caffeine levels, botanicals, alcohol-derived flavourings — need the same visible disclosure.",
      "Sampling an unlaunched product to a panel that includes people from the trade means competitors may be in the room, which is a confidentiality question before it is anything else.",
      "Adverse-reaction reports from sampling should reach the same place as consumer complaints on sold product, since both feed quality decisions and any withdrawal.",
      "Filmed tasting reactions are among the most useful creative a brand gets, and the permission has to cover paid advertising rather than only organic posting.",
    ],
    founderMistakes: [
      "Relying on a signed release instead of putting ingredient and allergen information in front of people before they tasted anything.",
      "Filming tasting reactions under a release covering internal use only, and being unable to run the best footage as an ad.",
      "Sampling an unlaunched formula at a trade event with no confidentiality term, and seeing it discussed publicly within days.",
      "Providing no route for a participant to report a reaction afterwards, so nothing was ever recorded.",
      "Publishing a participant's quote as a testimonial with no permission to do so.",
      "Sampling to minors at a public event with no thought given to consent.",
    ],
    negotiationPoints: [
      "Put the ingredient and allergen sheet in front of participants before the sample, and reference it in the release.",
      "Take a content and likeness permission broad enough for paid advertising if that is what you intend.",
      "Include a confidentiality term whenever an unlaunched product is being sampled.",
      "Ask counsel what the release can and cannot sensibly cover for your product and setting.",
      "Give participants a named contact and record any reaction reported.",
      "Set an age and consent position appropriate to the events you actually run.",
    ],
    redFlags: [
      "A release used as a substitute for disclosing allergens.",
      "Likeness permission narrower than the use you intend.",
      "No confidentiality term for an unlaunched product.",
      "No contact route or record process for an adverse reaction.",
      "Participant quotes published as testimonials with no permission.",
      "One form used unchanged across public sampling, filmed sessions and research panels.",
    ],
    beforeYouSign: [
      "The ingredient and allergen sheet for exactly what is in the cup that day.",
      "A decision on how footage and quotes will be used, so the permission matches the intention.",
      "A named contact and a record process for any reaction reported.",
      "Counsel's view on what the form should and should not attempt to do in your setting.",
    ],
    related: [
      "event-sampling-agreement",
      "sampling-agreement",
      "product-testing-participant-release",
      "model-release",
      "nil-release",
    ],
    attorneyReview: "yes",
    templateId: "product-sampling-release",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
];

export default EVENTS_DOCS;
